import { SOCKET_EVENTS, type ClientIntent, type HandRecord, type ServerError, type TableSnapshot } from '@tctp/protocol';
import { type Socket, io } from 'socket.io-client';

export type RoomStatus = 'connecting' | 'connected' | 'reconnecting' | 'refused';

/** A player's session token, or the operator monitor's key. */
export type RoomAuth = { token: string } | { monitorKey: string };

export interface RoomHandlers {
  onSnapshot(snapshot: TableSnapshot): void;
  /** The completed hands, sent once per connection (so a reconnect replaces the list). */
  onHistory?(hands: HandRecord[]): void;
  /** One more completed hand. */
  onHandRecord?(record: HandRecord): void;
  onError(error: ServerError): void;
  onStatus(status: RoomStatus, detail?: string): void;
}

export interface RoomConnection {
  /** Sends an intent; resolves once the server acknowledges it, rejects with `{ code, message }` when refused or unanswered. */
  act(intent: ClientIntent): Promise<void>;
  close(): void;
}

export interface ConnectOptions {
  /** How long `act()` waits for the server's acknowledgement before rejecting with `code: 'timeout'`. */
  ackTimeoutMs?: number;
}

const REFUSALS = new Set(['room_not_found', 'bad_token']);
const DEFAULT_ACK_TIMEOUT_MS = 10_000;

export function connectRoom(url: string | undefined, roomId: string, auth: RoomAuth, h: RoomHandlers, options: ConnectOptions = {}): RoomConnection {
  const ackTimeoutMs = options.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
  const socket: Socket = io(url ?? '/', { auth: { ...auth, roomId }, transports: ['websocket'] });
  h.onStatus('connecting');
  socket.on('connect', () => h.onStatus('connected'));
  socket.on('disconnect', () => h.onStatus('reconnecting'));
  socket.on('connect_error', err => {
    // A middleware rejection destroys the socket (socket.active becomes false) and never retries; transport errors keep retrying.
    if (!socket.active || REFUSALS.has(err.message)) {
      h.onStatus('refused', err.message);
      socket.close();
    } else {
      h.onStatus('reconnecting', err.message);
    }
  });
  socket.on(SOCKET_EVENTS.snapshot, (s: TableSnapshot) => h.onSnapshot(s));
  socket.on(SOCKET_EVENTS.history, (hands: HandRecord[]) => h.onHistory?.(hands));
  socket.on(SOCKET_EVENTS.handRecord, (record: HandRecord) => h.onHandRecord?.(record));
  socket.on(SOCKET_EVENTS.error, (e: ServerError) => h.onError(e));
  return {
    act: intent => new Promise((resolve, reject) => {
      socket.timeout(ackTimeoutMs).emit(SOCKET_EVENTS.intent, intent, (timedOut: Error | null, err?: ServerError | null) => {
        if (timedOut) reject(Object.assign(new Error('No response from the server'), { code: 'timeout' }));
        else if (err) reject(Object.assign(new Error(err.message), { code: err.code }));
        else resolve();
      });
    }),
    close: () => { socket.close(); },
  };
}
